import { useEffect } from 'react';

/**
 * Custom hook for managing window event listeners with automatic cleanup.
 *
 * Automatically adds an event listener to the window object when the component mounts
 * and removes it when the component unmounts or when dependencies change.
 *
 * @param {string} type - The event type to listen for (e.g., 'resize', 'scroll', 'keydown')
 * @param {EventListener} listener - The callback function to execute when the event occurs
 * @param {boolean | AddEventListenerOptions} [options] - Optional event listener options
 * @param {Object} [onLoad] - Optional callback to execute when the hook is loaded
 * @param {boolean} [options.capture] - Whether to capture the event during the capture phase
 * @param {boolean} [options.once] - Whether to remove the listener after it's called once
 * @param {boolean} [options.passive] - Whether the listener will never call preventDefault()
 * @param {AbortSignal} [options.signal] - AbortSignal to remove the listener
 *
 * @example
 * // Listen for window resize events
 * useWindowEvent('resize', (e) => {
 *   console.log('Window resized:', e.target.innerWidth);
 * });
 *
 * @example
 * // Listen for escape key presses - KeyboardEvent is properly typed
 * useWindowEvent('keydown', (e) => {
 *   if (e.key === 'Escape') {
 *     closeModal();
 *   }
 * });
 *
 * @example
 * // Mouse events are properly typed
 * useWindowEvent('click', (e) => {
 *   console.log('Clicked at:', e.clientX, e.clientY);
 * });
 *
 * @example
 * // Use with options
 * useWindowEvent('scroll', handleScroll, { passive: true });
 */
export const useWindowEvent = (type, listener, options, onLoad) => {
  useEffect(() => {
    if (onLoad && typeof onLoad === 'function') {
      onLoad();
    }
  }, [onLoad]);

  useEffect(() => {
    window.addEventListener(type, listener, options);
    return () => window.removeEventListener(type, listener, options);
  }, [type, listener, options]);
};
